import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";

export type DeleteUserOrganizationResponse = {
  success: boolean;
  message: string;
  data?: { userId?: string; organizationId?: string } | null;
};

export type DeleteUserOrganizationParams = {
  userId: string;
  organizationId: string;
};

const deleteUserOrganization = async (
  params: DeleteUserOrganizationParams
): Promise<DeleteUserOrganizationResponse> => {
  const { userId, organizationId } = params;
  const { data } = await customAxios.delete<DeleteUserOrganizationResponse>(
    `/users/${userId}/organization/${organizationId}`
  );
  return data;
};

export const useDeleteUserOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DeleteUserOrganizationResponse,
    AxiosError,
    DeleteUserOrganizationParams
  >({
    mutationFn: deleteUserOrganization,
    onSuccess: async (data, vars) => {
      showNotification({
        title: "Deleted",
        message: data.message ?? "Organization removed from user successfully",
        color: "green",
      });

      await queryClient.invalidateQueries(["users"]);
      await queryClient.invalidateQueries([
        "user",
        vars.userId,
        "organizations",
      ]);
      await queryClient.invalidateQueries([
        "user",
        vars.userId,
        "organization",
        vars.organizationId,
      ]);
    },
    onError: (error) =>
      showNotification({
        title: "Error removing organization",
        message:
          (error.response?.data as { message?: string })?.message ??
          "Failed to remove organization from user",
        color: "red",
      }),
  });
};
